import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/cn';
import { navItems } from '../../data/nav';
import { useIsCompact } from '../../hooks/useMediaQuery';
import { useScrolled } from '../../hooks/useScrolled';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { Logo } from './Logo';
import { MobileDrawer } from './MobileDrawer';
import styles from './Navbar.module.css';

/** A nav entry is active when its route matches, ignoring hash-only links. */
function useIsActive() {
  const { pathname, hash } = useLocation();
  return (to) => {
    if (!to) return false;
    if (to.startsWith('/#')) return pathname === '/' && hash === to.slice(1);
    return to === '/' ? pathname === '/' && !hash : pathname.startsWith(to);
  };
}

function AboutMenu({ item, isActive }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);

  // A hover-only menu is unusable by keyboard, so focus opens it too and
  // Escape/outside-click close it.
  const openNow = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    clearTimeout(closeTimer.current);
    // Grace period so the pointer can cross the gap to the panel.
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const childActive = item.children.some((c) => isActive(c.to));

  return (
    <div
      ref={wrapRef}
      className={cn(styles.menuWrap, open && styles.open)}
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
      onFocus={openNow}
      onBlur={(e) => {
        if (!wrapRef.current?.contains(e.relatedTarget)) closeSoon();
      }}
    >
      <button
        type="button"
        className={cn(styles.link, childActive && styles.active)}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
      >
        {item.label}
        <Icon name="chevron-down" size={16} className={styles.caret} />
      </button>

      {open && (
        <div className={styles.menu}>
          <div className={styles.menuList}>
            {item.children.map((child) => (
              <Link
                key={child.to}
                to={child.to}
                className={cn(styles.menuLink, isActive(child.to) && styles.menuActive)}
                onClick={() => setOpen(false)}
              >
                {child.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isCompact = useIsCompact();
  const isScrolled = useScrolled();
  const isActive = useIsActive();
  const { pathname } = useLocation();

  // Route changes must close the drawer, or it hangs over the new page.
  useEffect(() => setDrawerOpen(false), [pathname]);

  return (
    <header className={cn(styles.header, isScrolled && styles.scrolled)}>
      <div className={cn('container', styles.bar)}>
        <Logo />

        <nav className={styles.nav} aria-label="Main">
          {navItems.map((item) =>
            item.children ? (
              <AboutMenu key={item.label} item={item} isActive={isActive} />
            ) : (
              <Link
                key={item.label}
                to={item.to}
                className={cn(styles.link, isActive(item.to) && styles.active)}
              >
                {item.label}
                {isActive(item.to) && <span className={styles.dot} aria-hidden="true" />}
              </Link>
            ),
          )}
        </nav>

        <Button to="/contact" icon="arrow-right-circle" className={styles.cta}>
          Contact us
        </Button>

        <button
          type="button"
          className={styles.burger}
          aria-label="Open menu"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen(true)}
        >
          <Icon name="menu" size={22} />
        </button>
      </div>

      {isCompact && <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} isActive={isActive} />}
    </header>
  );
}
